




import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Chart } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import vue from 'vue';
import { colors } from '@/config';
import { highchartsSpacing } from '@/utils/chartPresentationSizing';

More(Highcharts);

@Component({ components: { Chart } })
export default class CustomLineChart extends Vue {
  @Prop() subSlide!: any;

  @Prop() dark!: boolean;
  @Prop() containerWidth: any;
  @Prop() currentLanguage: any;

  chartOptions: any = {};
  key: number = 0;

  update() {
    this.key++;
    // this.$nextTick(() => {
    //   const chart = this.$refs.chart as any;
    //   if (chart && chart.chart) {
    //     chart.chart.reflow();
    //   }
    // });

    const me = this.subSlide as any;
    this.chartOptions = {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        ...highchartsSpacing(this.containerWidth),
      },

      credits: {
        enabled: false,
      },

      title: {
        text: this.subSlide.diagramTitle || '',
        style: {
          fontSize: '2cqw',
          color: colors.Light,
        },
      },

      xAxis: {
        type: 'category',
        categories: this.getCategories(),
        labels: {
          autoRotation: [0],
          staggerLines: 1,
          padding: this.transformPx(10),
          style: {
            fontSize: '1cqw',
            textOverflow: 'none',
            whiteSpace: 'normal',
            color: colors.Light,
          },
        },
        lineColor: colors.Black,
      },

      yAxis: {
        title: {
          text: '',
        },
        labels: {
          style: {
            fontSize: '1cqw',
            color: colors.Light,
            fontWeight: 400,
          },
          formatter() {
            return (this as any).value + ' ' + me.diagramUnit;
          },
        },
        tickPixelInterval: this.transformPx(150),
        gridLineColor: colors.Black,
        plotLines: [
          {
            color: colors.Black,
            width: 2,
            value: 0,
            zIndex: 20,
          },
        ],
        softMin: 0,
        softMax: 0,
      },

      legend: {
        verticalAlign: 'top',
        align: 'right',
        enabled: true,
        itemStyle: {
          fontSize: '1cqw',
          fontWeight: 'normal',
          color: colors.Light,
        },
        itemHoverStyle: {
          color: colors.Light,
        },
        symbolWidth: this.transformPx(50),
      },

      tooltip: {
        headerFormat: '',
        formatter(): any {
          return (this as any).point.y + ' ' + me.diagramUnit;
        },
        style: {
          fontSize: '1.5cqw',
          color: colors.Light,
        },
        backgroundColor: colors.Dark,
      },

      series: this.getSeries(),
    };
  }

  mounted() {
    this.update();
  }

  @Watch('subSlide', { immediate: true, deep: true })
  subSlideChanged() {
    this.update();
  }

  @Watch('containerWidth', { immediate: false, deep: true })
  containerWidthChanged() {
    this.update();
  }

  getCategories(): string[] {
    if (!this.subSlide || !this.subSlide.categories) {
      return [];
    }

    const categories: string[] = [];
    for (const i of this.categoryIndexes) {
      categories.push(this.subSlide.categories[i] || '');
    }
    return categories;
  }

  get categoryIndexes(): number[] {
    if (!this.subSlide || !this.subSlide.categories ||
        !this.subSlide.values1 || !this.subSlide.values2 || !this.subSlide.values3) {
      return [];
    }

    const series = [
      this.subSlide.values1 || [],
      this.subSlide.values2 || [],
      this.subSlide.values3 || [],
    ];

    const categories: number[] = [];
    for (let i = 0; i < 12; i++) {
      if (this.subSlide.categories[i]) {
        categories.push(i);
        continue;
      }
      for (const serie of series) {
        const value = serie[i];
        if (value !== null && value !== undefined && value !== '') {
          categories.push(i);
          break;
        }
      }

    }
    return categories;
  }

  getSeriesData(values: any): number[] {
    if (!values) {
      return [];
    }

    const data: number[] = [];
    for (const i of this.categoryIndexes) {
      const value = values[i];
      if (value !== null && value !== undefined && value !== '') {
        data.push(parseFloat(value));
      } else {
        data.push(null as any);
      }
    }
    return data;
  }

  getSeries(): any[] {
    if (!this.subSlide) {
      return [];
    }

    const series: any[] = [];

    // Series 1
    if (this.subSlide.series1 && this.subSlide.series1.trim() !== '') {
      const data1 = this.getSeriesData(this.subSlide.values1);
      if (data1.length > 0) {
        series.push({
          name: this.subSlide.series1,
          color: colors.Secondary,
          dashStyle: 'solid',
          marker: {
            fillColor: colors.Secondary,
            lineWidth: this.transformPx(2),
            lineColor: colors.Secondary,
            radius: this.transformPx(8),
          },
          pointPadding: 0,
          lineWidth: this.transformPx(2),
          data: data1,
        });
      }
    }

    // Series 2
    if (this.subSlide.series2 && this.subSlide.series2.trim() !== '') {
      const data2 = this.getSeriesData(this.subSlide.values2);

      if (data2.length > 0) {
        series.push({
          name: this.subSlide.series2,
          color: colors.PrimaryLight,
          dashStyle: 'dash',
          marker: {
            fillColor: colors.PrimaryLight,
            lineWidth: this.transformPx(2),
            lineColor: colors.PrimaryLight,
            radius: this.transformPx(8),
          },
          pointPadding: 0,
          lineWidth: this.transformPx(2),
          data: data2,
        });
      }
    }

    // Series 3
    if (this.subSlide.series3 && this.subSlide.series3.trim() !== '') {
      const data3 = this.getSeriesData(this.subSlide.values3);

      if (data3.length > 0) {
        series.push({
          name: this.subSlide.series3,
          color: colors.Tertiary,
          dashStyle: 'dot',
          marker: {
            fillColor: colors.Tertiary,
            lineWidth: this.transformPx(2),
            lineColor: colors.Tertiary,
            radius: this.transformPx(8),
          },
          pointPadding: 0,
          lineWidth: this.transformPx(2),
          data: data3,
        });
      }
    }

    return series;
  }

  transformPx(value: any) {
    if (value * (this.containerWidth / 1920) < 1) {
      return 1;
    }
    return value * (this.containerWidth / 1920);
  }
}
